
import Vue from "vue";
import {slowMarkdown2Html} from "kistenkonfigurator/build/util/string-util";
import {chain, Dictionary} from "lodash";
import {CategoryNode, NavigationController} from "../../model/product/NavigationController";
import ProductInOverview from "../../components/product/ProductInOverview.vue";
import ContentTreeNavigation from "../../components/navigation/ContentTreeNavigation.vue";
import ContentBreadcrumb from "../../components/navigation/ContentBreadcrumb.vue";
import {DefaultProductComparator, ProductComparator} from "../../model/ProductBusinessLogic";
import ProductTiles from "../../components/product/ProductTiles.vue";
import {CategoryProduct, ProductDTO} from "../../model/rest/data-contracts";
import {GoogleTracking} from "../../model/google/GoogleTracking";
import SortProductSelect from "../../components/product/SortProductSelect.vue";
import {ProductValues} from "../../model/product/ProductValues";
import {ProductMetaModel} from "../../model/product/ProductMetaModel";
import Paginator from "../../components/product/Paginator.vue";

interface State {
  sortStrategy: ProductComparator | undefined;
  relevantProductIds: Array<number>;
  unsortedProducts: Array<ProductDTO>;
  productPageCache: Array<Array<ProductDTO>>;
  currentPage: number;
  perPage: number;
}

export default Vue.extend({
  components: {
    Paginator,
    SortProductSelect,
    ProductTiles,
    ContentBreadcrumb,
    ContentTreeNavigation,
    ProductInOverview,
  },
  fetchOnServer: true,
  async fetch(): Promise<void> {
    if (this.matchedNode == null) {
      return;
    }

    const categoryProducts: Array<CategoryProduct> = this.$bl.navigationRepository.getAllCategoryProductsById(this.matchedNode);
    this.relevantProductIds = chain(categoryProducts)
      .map((it: CategoryProduct) => it.productId)
      .uniq()
      .value();
    this.currentPage = this.relevantProductIds.length > 0 ? 1 : 0;
    await this.fetchProducts();
  },
  data(): State {
    return {
      sortStrategy: undefined,
      relevantProductIds: [],
      unsortedProducts: [] as Array<ProductDTO>,
      currentPage: 0,
      perPage: 100,
      productPageCache: [[]], // page = 0 is empty
    };
  },
  computed: {
    contentTree(): CategoryNode {
      return this.$store.getters["navigation/contentTree"];
    },
    matchedNode(): CategoryNode {
      const path = this.$route.params.pathMatch;
      let currentNode: CategoryNode = this.contentTree;
      // Path match only contains the path after "produkte/"
      let index = 0;

      while (index < path.length) {
        const loopIndex = index;
        for (let i = 0; i < currentNode.children.length; ++i) {
          const child = currentNode.children[i];
          const childUrlName = NavigationController.toURLNameNoEscape(child.name!);
          if (path.startsWith(childUrlName, index)) {
            currentNode = child as CategoryNode;
            index += childUrlName.length + 1; // skip any slashes inside the url
            break;
          }
        }
        if (loopIndex === index) {
          break;
        }
      }
      return currentNode;
    },
    description(): string {
      return slowMarkdown2Html(this.matchedNode.description != null ? this.matchedNode.description : "");
    },
    metaModels(): Dictionary<ProductMetaModel> {
      const result: Dictionary<ProductMetaModel> = {};

      this.unsortedProducts
        .map((it) => {
          let metaModel = result[it.typeId];
          if (metaModel == null) {
            metaModel = ProductMetaModel.createFromVue(this, this.$bl.navigationRepository.getProductType(it.typeId)!);
            result[it.typeId] = metaModel;
          }
        });
      return result;
    },
    products(): Array<ProductValues> {
      return this.unsortedProducts
        .map((it) => {
          const metaModel = this.metaModels[it.typeId];
          return new ProductValues(metaModel, it);
        });
    },
    productsWithPrices(): Array<ProductDTO> {
      const comparator = this.sortStrategy == null || this.sortStrategy.compare == null ? new DefaultProductComparator() : this.sortStrategy;

      return [...this.products].sort((a: ProductValues, b: ProductValues): number => {
        return comparator.compare(a, b);
      }).map(it => it.product);
    },
  },
  watch: {
    relevantProductIds(): void {
      if (this.relevantProductIds.length === 0) {
        this.currentPage = 0;
        return;
      }
      this.currentPage = 1;
    },
    currentPage(): void {
      this.fetchProducts();
    },
    productsWithPrices(newValue: Array<any>, oldValue: Array<any>): void {
      // Send Gtag event if there are products and the length of them did change (this prevents unnecessary events during sorting)
      if (this.productsWithPrices.length > 0 && (newValue.length !== oldValue.length)) {
        GoogleTracking.INSTANCE.notifyViewItemList(this.matchedNode, this.productsWithPrices);
      }
    },
  },
  mounted(): void {
    if (process.client) {
      document.getElementById("scrollTarget")?.scrollIntoView();
    }
  },
  methods: {
    sortProducts(sortStrategy: ProductComparator) {
      this.$set(this, "sortStrategy", sortStrategy); // sortStrategy is undefined initially...
    },
    async fetchProducts(): Promise<void> {
      const currentPage = this.currentPage;
      if (this.productPageCache[currentPage] == null) {
        const result = await this.$bl.productService.getByIds(this.relevantProductIds, this.perPage, this.currentPage);
        this.productPageCache[currentPage] = result.items;
      }
      this.unsortedProducts = this.productPageCache[currentPage];
    },
  },
  head(): any {
    return {
      title: this.$t("pages.produkte.pageTitle", {
        name: this.matchedNode.name,
        domain: this.$config.domain,
      }),
      meta: [
        // hid is used as a unique identifier. Do not use `vmid` for it as it will not work
        {
          hid: "description",
          name: "description",
          content: NavigationController.getHtmlHeadDescription(this.matchedNode),
        },
      ],
    };
  },
});
